import { useEffect } from 'react';

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
    return el;
  }
  if (attrs.content !== undefined) el.setAttribute('content', attrs.content);
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

export default function SEO({
  title,
  description,
  image,        // np. "/seo/diagnoza-si.png" (1200x630)
  pathname = '/', 
  type = 'article', // 'website' dla stron typu /kontakt
  siteName = 'Sensorini',
  noindex = false,
  // canonical={false} = strona nie ma wlasnego adresu kanonicznego.
  // Potrzebne dla 404: ten sam dokument odpowiada pod dowolnym adresem, wiec
  // KAZDY canonical bylby klamstwem. Bez tego strona 404 dziedziczy canonical
  // ze statycznego index.html, czyli wskazuje na strone glowna - i tak wlasnie
  // powstal raport "Duplikat, brak kanonicznego wybranego przez uzytkownika".
  canonical = true,
}) {
  useEffect(() => {
    const siteUrl = (import.meta.env?.VITE_SITE_URL || process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '');
    const canonicalUrl = `${siteUrl}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;
    const absImage = image?.startsWith('http') ? image : (image ? `${siteUrl}${image}` : undefined);

    if (title) document.title = title;

    // robots
    // noindex,follow (nie nofollow): strona ma zniknac z indeksu, ale linki
    // z niej maja dalej przekazywac ruch i sygnaly do stron ofertowych.
    upsertMeta('meta[name="robots"]', { name: 'robots', content: noindex ? 'noindex,follow' : 'index,follow' });

    // description
    if (description) upsertMeta('meta[name="description"]', { name: 'description', content: description });

    // Open Graph
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    if (title) upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    if (description) upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: siteName });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    if (absImage) {
      upsertMeta('meta[property="og:image"]', { property: 'og:image', content: absImage });
      upsertMeta('meta[property="og:image:type"]', { property: 'og:image:type', content: 'image/png' });
      upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: title || siteName });
    }

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    if (title) upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    if (description) upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    if (absImage) upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: absImage });

    // Canonical
    if (canonical) {
      upsertLink('canonical', canonicalUrl);
    } else {
      document.head.querySelector('link[rel="canonical"]')?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
    }
  }, [title, description, image, pathname, type, siteName, noindex, canonical]);

  return null;
}
